import {fold} from 'fp-ts/lib/Either';
import {pipe} from 'fp-ts/lib/function';
import * as t from 'io-ts';
import {PathReporter} from 'io-ts/lib/PathReporter';

import {UnreachableError} from '../../../libs/shared/core/src/helpers/types/customErrors';
import {makeEnumTypeGuard} from '../../../libs/shared/core/src/models/api/types/enumTypes';
import {
  HandoffContinuationError,
  HandoffErrorBannerTypesEnum,
} from '../../../libs/shared/core/src/models/handoff/handoffErrors';
import {RInboxesParameters} from '../../../src/store/panels/inboxes/parameters/inboxesParameters';

/*
 * Handoff activity types.
 */

// Enum values must match NSUser Activity values in Info.plist
export enum HandoffActivityTypesEnum {
  VIEWER = 'com.frontapp.front.viewer',
  MESSAGE_COMPOSE = 'com.frontapp.front.compose-message',
}

export const isHandoffActivityType = makeEnumTypeGuard(HandoffActivityTypesEnum);

/*
 * Runtime types.
 */

// Typing for user info property of NSUserActivity

export const RHandoffViewerRestorationData = t.readonly(
  t.type({
    parameters: RInboxesParameters,
  }),

  'handoffPayloadViewer',
);

export const RHandoffComposeMessageRestorationData = t.readonly(
  t.type({
    draftUid: t.string,
  }),
  'handoffPayloadMessageCompose',
);

// stateRestorationData is a JSON encoded string to preserve all the types.
// Otherwise, booleans are converted into 0 or 1 during the Handoff transfer
// because of the C in Objective-C...
// This will cause undefined properties to be dropped but they will be restored
// by io-ts in `decodeStateRestorationData()`.

export const RHandoffPayload = t.readonly(
  t.type({
    userId: t.Integer,
    stateRestorationData: t.string,
  }),
  'handoffPayload',
);

/*
 * Types.
 */

export interface HandoffViewerRestorationData
  extends Readonly<t.TypeOf<typeof RHandoffViewerRestorationData>> {}
export interface HandoffComposeMessageRestorationData
  extends Readonly<t.TypeOf<typeof RHandoffComposeMessageRestorationData>> {}
export interface HandoffPayload extends Readonly<t.TypeOf<typeof RHandoffPayload>> {}

export type HandoffStateRestorationData = HandoffViewerRestorationData | HandoffComposeMessageRestorationData;

// This type allows to match a given activity type with its specific payload.
export type RestorationData<T extends HandoffActivityTypesEnum> = T extends HandoffActivityTypesEnum.VIEWER
  ? HandoffViewerRestorationData
  : T extends HandoffActivityTypesEnum.MESSAGE_COMPOSE
    ? HandoffComposeMessageRestorationData
    : never;

export interface UserActivity {
  type: HandoffActivityTypesEnum;
  userInfo: HandoffPayload;
}

/*
 * Helpers.
 */

export function createUserActivity<T extends HandoffActivityTypesEnum>(
  type: T,
  data: RestorationData<T>,
  userId: number,
): UserActivity {
  return {type, userInfo: {stateRestorationData: JSON.stringify(data), userId}};
}

export function decodeHandoffPayload(userInfo: Record<string, any> | undefined) {
  if (!userInfo) {
    return undefined;
  }

  const onSuccess = (decodedUserInfo: HandoffPayload) => decodedUserInfo;

  const decodedPayload = RHandoffPayload.decode(userInfo);
  return pipe(decodedPayload, fold(buildPayloadErrorHandler(decodedPayload), onSuccess));
}

export function decodeStateRestorationData<T extends HandoffActivityTypesEnum>(
  type: T,
  payload: HandoffPayload,
): RestorationData<T> {
  const stateDataObject = JSON.parse(payload.stateRestorationData);
  const onSuccess = (restorationData: any) => restorationData;

  switch (type) {
    case HandoffActivityTypesEnum.VIEWER: {
      const decodedState = RHandoffViewerRestorationData.decode(stateDataObject);
      return pipe(decodedState, fold(buildPayloadErrorHandler(decodedState), onSuccess));
    }

    case HandoffActivityTypesEnum.MESSAGE_COMPOSE: {
      const decodedState = RHandoffComposeMessageRestorationData.decode(stateDataObject);
      return pipe(decodedState, fold(buildPayloadErrorHandler(decodedState), onSuccess));
    }

    default:
      throw new UnreachableError(type);
  }
}

function buildPayloadErrorHandler(result: t.Validation<any>) {
  return (_errors: t.Errors) => {
    const paths = PathReporter.report(result);
    throw new HandoffContinuationError('Failed to decode payload', {
      errorType: HandoffErrorBannerTypesEnum.HANDOFF_INVALID_PAYLOAD,
      paths,
    });
  };
}
